import React, { useEffect, useState, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Receipt, Download, Settings, Menu, X, Mail, ClipboardCheck, FileCheck, Sparkles, LogOut, Shield } from 'lucide-react';
import { useTheme } from '../lib/theme';
import { useAuth, hardLogout } from '../lib/auth';
import { getStats, getEmailQueueStats, getScore } from '../lib/api';
import CleoFloatingButton from './CleoFloatingButton';

const NAV = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/app/cleo', label: 'Cleo', icon: Sparkles },
  { to: '/app/receipts', label: 'Receipts', icon: Receipt, badge: 'pending' },
  { to: '/app/bookkeeper', label: 'Bookkeeper Mode', icon: ClipboardCheck, badge: 'issues' },
  { to: '/app/tax-ready', label: 'Tax Ready', icon: FileCheck },
  { to: '/app/reporting', label: 'Reporting', icon: Download },
  { to: '/app/settings', label: 'Settings', icon: Settings },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { colors } = useTheme();
  const isAdmin = user?.role === 'admin';
  const [pending, setPending] = useState(0);
  const [failedEmails, setFailedEmails] = useState(0);
  const [issuesTotal, setIssuesTotal] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = useCallback(() => {
    // Hard navigation avoids a React Router race where RequireAuth / Landing
    // can bounce us through `/login?redirect=/app` if the token clear and
    // the SPA navigation don't commit in the same render pass.
    hardLogout('/');
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [s, e, sc] = await Promise.all([
        getStats(),
        getEmailQueueStats().catch(() => null),
        getScore().catch(() => null),
      ]);
      setPending(s.pending);
      if (e) setFailedEmails(e.failed || 0);
      // Badge = actionable (high + medium) — excludes not_reviewed low noise.
      if (sc) setIssuesTotal((sc.issues?.high || 0) + (sc.issues?.medium || 0));
    } catch (err) {
      // Non-blocking background refresh (badges) — swallow to avoid noise
      console.error('Layout badge refresh failed:', err);
    }
  }, []);

  useEffect(() => {
    refresh();
    const int = setInterval(refresh, 15000);
    return () => clearInterval(int);
  }, [refresh]);

  // Close mobile nav on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isActive = (to) => {
    if (to === '/app') return location.pathname === '/app' || location.pathname === '/app/';
    return location.pathname.startsWith(to);
  };

  return (
    <div className="flex h-screen" style={{ backgroundColor: colors.bg }}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 flex flex-col
          border-r transition-transform duration-200
          lg:translate-x-0
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}
      >
        <div className="flex items-center gap-3 px-6 h-16 border-b" style={{ borderColor: colors.border }}>
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: colors.accent }}
          >
            C
          </div>
          <span
            className="text-lg font-bold tracking-tight"
            style={{ color: colors.text }}
            data-testid="brand-name"
          >
            Clasifi
          </span>
        </div>

        {isAdmin && (
          <div className="px-4 pt-3" data-testid="admin-dashboard-link-wrap">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              data-testid="sidebar-admin-dashboard"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
              style={{
                color: colors.accent,
                border: `1px solid ${colors.accent}`,
                backgroundColor: 'transparent',
                boxShadow: `0 0 0 0 ${colors.accentSoft}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.accentSoft;
                e.currentTarget.style.boxShadow = `0 8px 20px -10px ${colors.accent}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = `0 0 0 0 ${colors.accentSoft}`;
              }}
            >
              <Shield size={14} />
              Admin dashboard
            </button>
          </div>
        )}

        <nav className="flex-1 py-4 px-3 flex flex-col gap-1" data-testid="sidebar-nav">
          {NAV.map(({ to, label, icon: Icon, badge }) => {
            const active = isActive(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase()}`}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-colors duration-150 relative
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                `}
                style={{
                  backgroundColor: active ? colors.accentSoft : 'transparent',
                  color: active ? colors.accent : colors.textMuted,
                }}
              >
                <Icon size={20} />
                <span>{label}</span>
                {badge === 'pending' && pending > 0 && (
                  <span
                    data-testid="nav-pending-badge"
                    className="ml-auto min-w-[20px] h-5 flex items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white"
                    style={{ backgroundColor: colors.accent }}
                  >
                    {pending > 99 ? '99+' : pending}
                  </span>
                )}
                {badge === 'failed' && failedEmails > 0 && (
                  <span
                    data-testid="nav-failed-emails-dot"
                    className="ml-auto min-w-[20px] h-5 flex items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white"
                    style={{ backgroundColor: colors.error }}
                  >
                    {failedEmails > 99 ? '99+' : failedEmails}
                  </span>
                )}
                {badge === 'issues' && issuesTotal > 0 && (
                  <span
                    data-testid="nav-issues-badge"
                    className="ml-auto min-w-[20px] h-5 flex items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white"
                    style={{
                      backgroundColor:
                        issuesTotal > 20 ? colors.error
                        : issuesTotal >= 5 ? colors.warning
                        : colors.success,
                    }}
                  >
                    {issuesTotal > 99 ? '99+' : issuesTotal}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="px-4 py-3 border-t flex items-center gap-3" style={{ borderColor: colors.border }} data-testid="sidebar-user">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
            style={{ backgroundColor: colors.accentSoft, color: colors.accent }}
            aria-hidden
          >
            {(user?.display_name || user?.name || 'U')[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate" style={{ color: colors.text }}>{user?.display_name || user?.name || 'User'}</div>
            <div className="text-[11px] truncate" style={{ color: colors.textMuted }}>{user?.email}</div>
          </div>
          <button
            onClick={onLogout}
            data-testid="sidebar-logout"
            title="Sign out"
            className="p-2 rounded-lg transition-colors hover:opacity-80"
            style={{ color: colors.textMuted }}
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center gap-3 px-4 h-14 border-b" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 rounded-lg transition-colors hover:opacity-80"
            style={{ color: colors.text }}
          >
            <Menu size={20} />
          </button>
          <span className="text-base font-bold tracking-tight" style={{ color: colors.text }}>Clasifi</span>
        </div>

        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>

      {/* Global floating Cleo button */}
      <CleoFloatingButton />
    </div>
  );
}
